import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, Menu, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { useAuthStore } from '@/store/authStore';
import { getUnacknowledgedSummary } from '@/services/db/alerts';
import type { AlertSeverity } from '@/types/alerts';

const ROUTE_LABELS: Record<string, string> = {
  '/home': 'Platform Home',
  '/dashboard': 'Dashboard',
  '/reports': 'DSR Reports',
  '/alerts': 'Alerts',
  '/settings': 'Settings',
  '/workforce': 'Workforce',
  '/analytics': 'Analytics',
  '/admin': 'Admin',
  '/platform-admin': 'Platform Admin',
  '/profile': 'Profile',
  '/dsr/new': 'New DSR',
};

function pageLabel(pathname: string): string | null {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  if (pathname.startsWith('/reports/') && pathname.endsWith('/amend')) return 'Amend DSR';
  if (pathname.startsWith('/reports/')) return 'DSR Detail';
  if (pathname.startsWith('/dsr/') && pathname.endsWith('/edit')) return 'Edit DSR';
  return null;
}

interface TopBarProps {
  onMobileMenuOpen: () => void;
}

function badgeClassFor(topSeverity: AlertSeverity | null): string {
  if (topSeverity === 'critical' || topSeverity === 'high') {
    return 'bg-red-600 text-white';
  }
  if (topSeverity === 'medium') return 'bg-amber-500 text-white';
  if (topSeverity === 'low') return 'bg-gray-500 text-white';
  return 'bg-muted text-muted-foreground';
}

export default function TopBar({ onMobileMenuOpen }: TopBarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userRole, signOut, tenantId } = useAuthStore();

  const alertSummary = useQuery({
    queryKey: ['alerts', 'unack-summary', tenantId],
    queryFn: () => getUnacknowledgedSummary(tenantId as string),
    enabled: !!tenantId,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const total = alertSummary.data?.total ?? 0;
  const topSeverity = alertSummary.data?.top_severity ?? null;

  const isHome = location.pathname === '/home';
  const currentLabel = pageLabel(location.pathname) ?? 'Dashboard';

  const initials = user?.email
    ? user.email.substring(0, 2).toUpperCase()
    : '??';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <header className="flex h-14 items-center border-b bg-white px-4">
      {/* Mobile hamburger */}
      <button
        onClick={onMobileMenuOpen}
        className="mr-3 md:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5 text-muted-foreground" />
      </button>

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm">
        {isHome ? (
          <span className="font-medium text-foreground">Platform Home</span>
        ) : (
          <>
            <Link
              to="/home"
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              Platform Home
            </Link>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60" />
            <span className="font-medium text-foreground">{currentLabel}</span>
          </>
        )}
      </nav>

      <div className="flex-1" />

      {/* Notifications */}
      <button
        onClick={() => navigate('/alerts')}
        className="relative p-2 text-muted-foreground hover:text-foreground"
        aria-label={total > 0 ? `${total} unacknowledged alerts` : 'Alerts'}
      >
        <Bell className="h-5 w-5" />
        {total > 0 && (
          <span
            className={`absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold ${badgeClassFor(topSeverity)}`}
          >
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      <Separator orientation="vertical" className="mx-2 h-6" />

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent">
            <Avatar className="h-7 w-7">
              <AvatarFallback className="bg-mozaic-navy text-xs text-white">
                {initials}
              </AvatarFallback>
            </Avatar>
            <span className="hidden text-sm sm:inline">{user?.email}</span>
            {userRole && (
              <Badge className="hidden bg-mozaic-teal/15 text-mozaic-teal text-[10px] capitalize sm:inline-flex">
                {userRole.replace('_', ' ')}
              </Badge>
            )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem disabled>Profile</DropdownMenuItem>
          <DropdownMenuItem onClick={handleSignOut}>Sign Out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
